import React, { useRef, useState } from "react";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { TbDownload } from "react-icons/tb";
import toast from "react-hot-toast";
import { assets } from "../../../Assets/assets";

// Assume these are in your assets
// import logo from "../../../assets/qqtlogo.png";

const DeclarationDetailCard = ({ declaration }) => {
  const pdfContentRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);

  if (!declaration) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[300px] text-center p-8">
        <div className="w-16 h-16 rounded-2xl bg-[#772037]/8 flex items-center justify-center mb-4">
          <span className="text-3xl">📋</span>
        </div>
        <p className="text-gray-400 font-medium text-sm">Select a declaration to view details</p>
      </div>
    );
  }

  // Format dates
  const fmtDate = (d) => d
    ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })
    : '—';

  const fmtDateTime = (d) => d
    ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';

  const getReasonDisplay = (reason) => {
    switch (reason) {
      case 'Term_And_Condition':
        return 'Terms & Conditions Agreement';
      case 'Expected_Delivery_Time':
        return 'Expected Delivery Time Acceptance';
      case 'Single_Quotation':
        return 'Quotation Declaration';
      case 'Cart_Of_Quotations':
        return 'Multi-Quotation Declaration';
      default:
        return reason || 'Declaration';
    }
  };

  const getDeclarationType = (type) => {
    switch (type) {
      case 'Term_And_Condition':
        return '📋';
      case 'Expected_Delivery_Time':
        return '📅';
      case 'Single_Quotation':
        return '💰';
      case 'Cart_Of_Quotations':
        return '🛒';
      default:
        return '📄';
    }
  };

  const handleDownloadPDF = async () => {
    if (!pdfContentRef.current) return;

    setIsDownloading(true);
    try {
      const canvas = await html2canvas(pdfContentRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.85);
      const pdf = new jsPDF({
        orientation: "p",
        unit: "mm",
        format: "a4",
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight, undefined, "FAST");
      heightLeft -= pdfHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, position, imgWidth, imgHeight, undefined, "FAST");
        heightLeft -= pdfHeight;
      }

      pdf.save(`Declaration_${getReasonDisplay(declaration.reason)}_${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success("PDF downloaded successfully");
    } catch (error) {
      console.error("PDF generation failed:", error);
      toast.error("Failed to generate PDF. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const statusStyle = declaration.e_sign
    ? { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500', label: 'Signed' }
    : { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-400', label: 'Pending' };

  return (
    <div className="h-full overflow-y-auto space-y-4 pb-6 pr-0.5">

      {/* ══ HEADER BANNER ════════════════════════════════ */}
      <div className="bg-gradient-to-br from-[#772037] to-[#CC234F] rounded-2xl p-4 sm:p-5 text-white shadow-lg shadow-[#772037]/20">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <p className="text-[10px] font-bold text-white/50 uppercase tracking-widest mb-1">Declaration Type</p>
            <h2 className="text-lg sm:text-xl font-bold tracking-tight flex items-center gap-2">
              <span>{getDeclarationType(declaration.declaration_is_for)}</span>
              {getReasonDisplay(declaration.reason)}
            </h2>
            <p className="text-xs text-white/60 mt-0.5">Created on {fmtDate(declaration.createdAt)}</p>
          </div>
          <div className="flex flex-col sm:items-end gap-2">
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border ${statusStyle.bg} ${statusStyle.text} ${statusStyle.border}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${statusStyle.dot} ${!declaration.e_sign ? 'animate-pulse' : ''}`} />
              {statusStyle.label}
            </span>
          </div>
        </div>

        {/* Download Button */}
        <div className="mt-4 flex gap-2">
          <button
            onClick={handleDownloadPDF}
            disabled={isDownloading}
            className="flex items-center gap-2 bg-white text-[#772037] hover:bg-white/90 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 rounded-lg text-sm font-bold shadow-sm transition"
          >
            <TbDownload size={16} />
            {isDownloading ? 'Generating...' : 'Download PDF'}
          </button>
        </div>
      </div>

      {/* ══ INFO SECTION ══════════════════════════════════ */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-5 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Declaration Type */}
          <div className="flex flex-col">
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Declaration For</span>
            <span className="text-sm font-semibold text-gray-800">
              {declaration.declaration_is_for?.replace(/_/g, ' ') || '—'}
            </span>
          </div>

          {/* Reason */}
          <div className="flex flex-col">
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Reason</span>
            <span className="text-sm font-semibold text-gray-800">
              {declaration.reason || '—'}
            </span>
          </div>

          {/* Created Date */}
          <div className="flex flex-col">
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Created Date</span>
            <span className="text-sm font-semibold text-gray-800">
              {fmtDateTime(declaration.createdAt)}
            </span>
          </div>

          {/* Reference Date */}
          <div className="flex flex-col">
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Reference Date</span>
            <span className="text-sm font-semibold text-gray-800">
              {fmtDate(declaration.date)}
            </span>
          </div>

          {/* Order ID */}
          {declaration.orderId && (
            <div className="flex flex-col">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Order ID</span>
              <span className="text-sm font-semibold text-gray-800">
                #{declaration.orderId.toString().slice(-6)}
              </span>
            </div>
          )}

          {/* Quotation ID */}
          {declaration.quotationId && (
            <div className="flex flex-col">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Quotation ID</span>
              <span className="text-sm font-semibold text-gray-800">
                #{declaration.quotationId.toString().slice(-6)}
              </span>
            </div>
          )}

          {/* Signature Status */}
          <div className="flex flex-col">
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Signature Status</span>
            <span className={`text-sm font-semibold ${declaration.e_sign ? 'text-emerald-600' : 'text-amber-600'}`}>
              {declaration.e_sign ? '✅ Signed' : '⏱️ Not Signed'}
            </span>
          </div>
        </div>
      </div>

      {/* ══ DECLARATION CONTENTS ══════════════════════════ */}
      {declaration.declaration_contents?.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-4 sm:px-5 py-3 border-b border-gray-100 flex items-center gap-2 bg-gray-50/70">
            <span className="text-sm">📄</span>
            <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Key Declarations</h3>
          </div>
          <div className="px-4 sm:px-5 py-4 space-y-3">
            {declaration.declaration_contents.map((item, idx) => {
              const lowerContent = (item.content || '').toLowerCase().trim();
              const isIntro = lowerContent.startsWith('i hereby declare') || lowerContent.startsWith('we hereby declare');

              if (isIntro) {
                return (
                  <p key={idx} className="font-bold text-gray-800 text-sm">
                    {item.content}
                  </p>
                );
              }

              return (
                <div key={idx} className="flex gap-3 items-start">
                  <div className="w-4 h-4 rounded border border-[#772037] bg-[#772037]/5 flex-shrink-0 flex items-center justify-center mt-0.5">
                    {item.isChecked && (
                      <svg className="w-2.5 h-2.5 text-[#772037]" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed pt-0.5">{item.content}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ══ SECTIONS (Terms, etc.) ════════════════════════ */}
      {declaration.sections?.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-4 sm:px-5 py-3 border-b border-gray-100 flex items-center gap-2 bg-gray-50/70">
            <span className="text-sm">📋</span>
            <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">Terms & Sections</h3>
          </div>
          <div className="px-4 sm:px-5 py-4 space-y-4">
            {declaration.sections.map((section, sIdx) => (
              <div key={sIdx} className="border border-gray-100 rounded-lg p-3 bg-gray-50/50">
                {/* Section Heading */}
                <div className="bg-gray-100 px-3 py-1.5 rounded font-bold text-[11px] text-gray-800 mb-2 uppercase tracking-wide">
                  {section.heading}
                </div>

                {/* Section Intro */}
                {section.intro && (
                  <p className="text-xs italic text-gray-500 mb-2 pl-2 leading-relaxed">
                    {section.intro}
                  </p>
                )}

                {/* Groups */}
                {section.groups?.length > 0 && (
                  <div className="space-y-2 pl-2">
                    {section.groups.map((group, gIdx) => (
                      <div key={gIdx} className="space-y-1">
                        {group.subHeading && (
                          <div className="text-[9.5px] font-bold text-[#772037] uppercase tracking-wider">
                            {group.subHeading}
                          </div>
                        )}

                        {group.items?.length > 0 && (
                          <ul className="space-y-1 list-none">
                            {group.items.map((item, iIdx) => (
                              <li key={iIdx} className="flex gap-2 items-start text-xs text-gray-600 leading-relaxed">
                                <span className="w-1.5 h-1.5 bg-[#772037] rounded-full mt-1.5 flex-shrink-0" />
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        )}

                        {group.note && (
                          <div className="bg-amber-50/60 border border-amber-200/60 text-amber-800 text-[10px] italic p-2 rounded-lg mt-1 inline-block">
                            {group.note}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ══ E-SIGNATURE ════════════════════════════════════ */}
      {declaration.e_sign && (
        <div className="bg-gradient-to-br from-emerald-50 to-emerald-50 rounded-2xl border border-emerald-200 shadow-sm p-4 sm:p-5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-bold text-emerald-900">Document Signed</p>
                <p className="text-xs text-emerald-700">This declaration has been digitally signed and is legally binding.</p>
              </div>
            </div>
            <div className="min-h-[50px] flex items-center">
              {declaration.e_sign && (
                <img
                  src={declaration.e_sign}
                  alt="Digital Signature"
                  className="h-12 max-w-[150px] object-contain mix-blend-multiply"
                />
              )}
            </div>
          </div>
        </div>
      )}

      <div
        ref={pdfContentRef}
        style={{
          position: 'fixed',
          left: '-10000px',
          top: 0,
          width: '210mm',
          backgroundColor: '#ffffff',
        }}
      >
        <PDFContent declaration={declaration} getReasonDisplay={getReasonDisplay} fmtDate={fmtDate} fmtDateTime={fmtDateTime} />
      </div>

    </div>
  );
};

// ─── PDF Content Component ────────────────────────────
const PDFContent = ({ declaration, getReasonDisplay, fmtDate, fmtDateTime }) => {
  return (
    <div className="max-w-3xl mx-auto bg-white p-8" style={{ minHeight: "1122px" }}>
      {/* Header */}
      <div className="bg-[#CC234F] p-5 flex justify-between items-center text-white">
        <div className="flex items-center gap-3">
          <div className="bg-white p-1.5 rounded">
            <img src={assets.qqtlogo} alt="QQT Logo" className="h-10 object-contain block" />
          </div>
          <div className="text-left">
            <div className="text-lg font-bold tracking-wide">PCM EXPORTS</div>
            <div className="text-[10px] text-white/80 font-medium">Quick Quotation & Trade (QQT)</div>
          </div>
        </div>
        <div className="text-right text-[10px] leading-relaxed opacity-95">
          <div>+91-6752455372</div>
          <div>pcmexportsbbsr@gmail.com</div>
          <div>www.pcmexports.com</div>
        </div>
      </div>

      {/* Address Bar */}
      <div className="bg-[#FDF2F4] p-2 text-center text-[9.5px] text-gray-600 font-medium border-b border-gray-100 leading-normal">
        Shop No. 104, Block-A, Bharati Tower, Forestpark Ekamra Marg, Bhubaneswar, Khordha, Odisha(India) - 751009
      </div>

      {/* GSTIN & PAN */}
      <div className="px-4 py-2 flex justify-between items-center text-[10px] font-bold border-b border-gray-100">
        <span className="text-[#CC234F]">GSTIN: 21BBFPM2533K1ZW</span>
        <span className="text-[#CC234F]">PAN: BILPS0249G</span>
      </div>

      {/* Title */}
      <div className="m-4 p-4 text-center bg-[#FDF2F4] rounded-lg border border-pink-200">
        <h1 className="text-lg font-extrabold text-[#CC234F] uppercase tracking-wider">
          {getReasonDisplay(declaration.reason)}
        </h1>
        <p className="text-[10.5px] text-gray-500 mt-1 font-medium">
          Effective Date: {fmtDate(declaration.createdAt)}
        </p>
      </div>

      {/* Sections */}
      {declaration.sections?.length > 0 && (
        <div className="mx-4 mb-6 space-y-4">
          {declaration.sections.map((section, sIdx) => (
            <div key={sIdx} className="text-left border border-gray-100 rounded-lg p-3 bg-gray-50/50">
              <div className="bg-gray-100 px-2 py-1 rounded font-bold text-[10px] text-gray-800 mb-2 uppercase tracking-wide">
                {section.heading}
              </div>
              {section.intro && (
                <p className="text-[10px] italic text-gray-500 mb-2 pl-2 leading-tight">
                  {section.intro}
                </p>
              )}
              {section.groups?.length > 0 && (
                <div className="space-y-2 pl-2">
                  {section.groups.map((group, gIdx) => (
                    <div key={gIdx} className="space-y-1">
                      {group.subHeading && (
                        <div className="text-[9px] font-bold text-[#CC234F] uppercase">
                          {group.subHeading}
                        </div>
                      )}
                      {group.items?.length > 0 && (
                        <ul className="space-y-1">
                          {group.items.map((item, iIdx) => (
                            <li key={iIdx} className="flex gap-2 items-start text-[10px] text-gray-600 leading-tight">
                              <span className="w-1 h-1 bg-[#CC234F] rounded-full mt-1 flex-shrink-0" />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Declaration Contents */}
      <div className="mx-4 mb-6">
        <div className="bg-[#FDF2F4] px-2 py-1 rounded font-bold text-[10px] text-[#CC234F] mb-3 uppercase">
          Key Declarations
        </div>
        {declaration.declaration_contents?.length > 0 ? (
          <div className="space-y-2 text-left">
            {declaration.declaration_contents.map((item, idx) => {
              const lowerContent = (item.content || '').toLowerCase().trim();
              const isIntro = lowerContent.startsWith('i hereby declare') || lowerContent.startsWith('we hereby declare');

              if (isIntro) {
                return (
                  <p key={idx} className="font-bold text-gray-800 text-[11px]">
                    {item.content}
                  </p>
                );
              }

              return (
                <div key={idx} className="flex gap-2 items-start">
                  <div className="w-3 h-3 rounded border border-[#CC234F] bg-[#FDF2F4] flex-shrink-0 flex items-center justify-center mt-0.5">
                    {item.isChecked && (
                      <svg className="w-2 h-2 text-[#CC234F]" fill="none" stroke="currentColor" strokeWidth="4" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <p className="text-[10px] text-gray-700 leading-tight">{item.content}</p>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>

      {/* Footer */}
      <div className="text-center text-[9px] text-gray-400 mt-8 border-t border-gray-100 pt-4">
        This is an electronically generated and legally binding agreement certified via the QQT Platform of PCM Exports.
      </div>
    </div>
  );
};

export default DeclarationDetailCard;